import {
  filterRepliesByQuestionIds,
  getRawReplyValue,
  getReplyValue,
  getFieldValueByIdOrFallback,
  FALLBACK
} from '../../_helpers/pdfHelpers'
import {
  CARGO_QUESTION_IDS,
  NUM_FUNCIONARIOS_QUESTION_IDS,
  SETOR_QUESTION_IDS,
  POSSIVEL_RISCO_QUESTION_IDS,
  INVENTARIO_PROBABILIDADE_QUESTION_IDS,
  INVENTARIO_SEVERIDADE_QUESTION_IDS,
  ATIVO_FIELD_ID_DESCRICAO_CARGO
} from './mappingIds'
import { REGRA_RUIDO, REGRA_TEMPERATURA, REGRA_ILUMINANCIA, calcularNiveisDeRisco } from './riskEngine'
import { LINHAS_INVENTARIO_RISCOS_FIXAS } from './inventarioRiscosFixo'
import { resolveLinhaPsicossocial } from './psicossocialRiskEngine'

/**
 * Construção do Inventário de Riscos por GHE (Setor + Cargo).
 *
 * Seção 18 (ver `sections/18.InventarioRiscosSection.js`): confirmado contra o modelo de
 * referência real (56 páginas, Seção 18 nas páginas 42-51) que o Inventário é sempre
 * UMA tabela por CARGO (nunca agrupado por Setor — dois cargos do mesmo Setor, ex.
 * "Gerencia Negócios e Atendimento" e "Supervisor Administrativo" dentro de "Gerência
 * Administrativa", geram dois blocos separados) e que cada tabela tem sempre a MESMA
 * lista fixa de 13 linhas de risco (`_helpers/inventarioRiscosFixo.js`), nesta ordem,
 * com texto institucional fixo (Tipo de exposição/Fonte geradora/Possíveis lesões/
 * Embasam. Técnico/Medidas de controle) — nunca lida da atividade, nunca omitida por
 * falta de resposta. As únicas colunas realmente dinâmicas são Nº func. expostos
 * (nível de Cargo) e, por linha, Probabilidade/Severidade/Nível de Risco.
 *
 * IMPORTANTE (limitação assumida, ver PR): a spec fornecida define os IDs de
 * Probabilidade/Severidade/"Possível Risco" como listas globais pareadas por posição,
 * mas não define explicitamente a qual Cargo cada posição pertence, nem uma lista de
 * IDs específica por linha fixa (ex.: um ID dedicado para "Repetitividade"). Este
 * módulo resolve a primeira associação (posição -> Cargo) subindo a árvore de
 * `question.parentReference` a partir de cada resposta até encontrar o Cargo ancestral
 * mais próximo — o mesmo padrão já usado pelo template de referência (ver
 * bioqualynet/pgrTable/sections/00.PgrTable.js, `findParentReply`/
 * `filterRepliesByParentReference`) e pelo padrão `blockIndex`
 * (allarmi/_helpers/formatBlocks.js). A segunda associação (posição -> linha fixa) é
 * resolvida casando o NOME do risco respondido em "Possível Risco" (multi-select) com
 * o nome de cada linha fixa (`encontrarInstanciaPorNome` abaixo) — ambas devem ser
 * validadas com uma atividade real do Bradesco antes de ir para produção.
 */

const getDescendantQuestionIds = (replies, rootReference, visited = new Set()) => {
  if (!rootReference || visited.has(rootReference)) return visited
  visited.add(rootReference)
  replies
    .filter(reply => reply.question?.parentReference === rootReference)
    .forEach(reply => {
      if (reply.question?.id !== undefined) visited.add(`q:${reply.question.id}`)
      getDescendantQuestionIds(replies, reply.question?.reference, visited)
    })
  return visited
}

const belongsToCargo = (descendantSet, questionId) => descendantSet.has(`q:${questionId}`)

const walkUpToAncestorAmong = (replies, startReply, targetQuestionIds, maxDepth = 8) => {
  let current = startReply
  let depth = 0
  while (current && depth < maxDepth) {
    if (targetQuestionIds.includes(current.question?.id)) {
      return current
    }
    const parentRef = current.question?.parentReference
    if (!parentRef) return null
    current = replies.find(reply => reply.question?.reference === parentRef)
    depth++
  }
  return null
}

/**
 * Uma resposta é "genuinamente ausente" quando o reply não existe, ou existe mas seu
 * valor bruto é `undefined`/`null`/`''` — nunca quando existe com um valor inesperado
 * (isso é um problema de qualidade de dado diferente, fora do escopo do fallback abaixo).
 */
const isValorAusente = valorBruto => valorBruto === undefined || valorBruto === null || valorBruto === ''

/**
 * Nome de um item de opção do sistema (`{ id, name }`) ou o próprio valor primitivo.
 */
const nomeDeItemDeOpcao = item => (item && typeof item === 'object' ? item.name ?? item.value : item)

/**
 * Nome(s) individuais de UMA resposta que pode ser seleção única OU múltipla (checkbox) —
 * usado tanto para Cargo (GHE) quanto para Setor (256), ambas perguntas do mesmo tipo
 * "checkbox" que podem receber mais de uma seleção na mesma resposta. Diferente de
 * `getReplyValue` (que reduz uma seleção múltipla a UMA string unindo os nomes com ", ",
 * apropriado só para exibição em rótulo único), esta função devolve cada nome
 * selecionado como seu próprio item de lista — necessário sempre que cada seleção deve
 * gerar sua própria linha/tabela/entrada, nunca mescladas (ver `getNomesCargoDaResposta`/
 * `getListaDeSetoresRespondidos` abaixo).
 */
const getNomesDaResposta = reply => {
  const valorBruto = getRawReplyValue(reply)

  if (Array.isArray(valorBruto)) {
    return valorBruto.map(nomeDeItemDeOpcao).filter(nome => nome !== undefined && nome !== null && nome !== '')
  }

  const nomeUnico = getReplyValue(reply)
  return nomeUnico === FALLBACK ? [] : [nomeUnico]
}

/**
 * Entre vários candidatos de reply para a MESMA pergunta (várias repetições de um bloco
 * repetível, ver `getRespostasDeCargo`/`buildCargoInventario` abaixo para a causa raiz
 * completa — regra definitiva de Julia Buzzo, PM, 2026-09-03), escolhe o candidato desta
 * repetição específica pelo `blockIndex` compartilhado. Comportamento IDÊNTICO ao anterior
 * (`findReplyByQuestionId`/primeiro encontrado) quando há 0 ou 1 candidato, ou quando
 * `blockIndex` não está definido — só passa a escolher pelo `blockIndex` quando existe MAIS
 * de um candidato E ele está definido, o caso novo que motivou esta função. Nunca lança;
 * sem candidato que bata o `blockIndex`, cai no primeiro (nunca perde o dado, mesma
 * postura de fallback do resto do módulo).
 */
const preferirPorBlockIndex = (candidatos, blockIndex) => {
  if (candidatos.length <= 1 || blockIndex === undefined) return candidatos[0]
  return candidatos.find(candidato => candidato.blockIndex === blockIndex) ?? candidatos[0]
}

/**
 * Remove acentos, caixa e pontuação — usado só para CASAR nomes de risco (nunca para
 * exibição). Sem isso, variações de pontuação entre o texto fixo do modelo ("Mov.
 * Manual de Cargas – Transporte", en-dash) e a opção como o formulário da atividade
 * realmente cadastra o mesmo risco (ex.: hífen comum, sem o "Mov.") fariam a linha cair
 * sempre no default de ausência mesmo quando a atividade respondeu aquele risco.
 */
const normalizarNomeRisco = texto =>
  typeof texto === 'string'
    ? texto
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
    : texto

/**
 * Nome(s) de risco CANDIDATOS para esta instância de Probabilidade/Severidade — usado
 * só para CASAR com o nome de uma linha fixa (`encontrarInstanciaPorNome` abaixo),
 * nunca para exibição (a coluna "Possível Risco" exibida é sempre o texto fixo da
 * própria linha, ver `inventarioRiscosFixo.js`).
 *
 * "Possível Risco" é multi-select. Duas formas já observadas de a atividade estruturar
 * isso (nenhuma confirmada como A real sem uma atividade de produção — ver LIMITAÇÃO no
 * topo do arquivo):
 *
 * 1) Uma resposta de "Possível Risco" ANCESTRAL EXCLUSIVA de uma única instância de
 *    Probabilidade/Severidade (seleção única, ou múltipla mas toda ela pertencente a
 *    esta mesma instância) — mesmo padrão já revisado antes desta reescrita (ver
 *    histórico do PR fix/bradesco-pgr-possivel-risco-lookup): todos os nomes da seleção
 *    são candidatos.
 * 2) Uma ÚNICA resposta de "Possível Risco" (um array com mais de um item) ancestral de
 *    VÁRIAS instâncias de Probabilidade/Severidade, uma por risco selecionado,
 *    distinguidas por `blockIndex` — a posição do risco dentro do array de itens
 *    selecionados. Neste caso o candidato é SÓ o item nessa posição — nunca a lista
 *    inteira, senão toda instância casaria com toda linha fixa cujo nome apareça em
 *    QUALQUER item da seleção compartilhada (ex.: as 3 linhas "Mov. Manual de Cargas
 *    ..." casariam todas com a mesma primeira instância encontrada).
 *
 * Só cai no caso (2) quando `blockIndex` é mesmo um índice válido do array — fora
 * disso, cai no (1), preservando o comportamento já revisado.
 */
const getNomesRiscoCandidatos = (replies, instanciaReply) => {
  const ancestor = walkUpToAncestorAmong(replies, instanciaReply, POSSIVEL_RISCO_QUESTION_IDS)
  if (!ancestor) return []

  const valorBruto = getRawReplyValue(ancestor)
  const nomeDeItem = item => (item && typeof item === 'object' ? item.name ?? item.value : item)
  const blockIndex = instanciaReply?.blockIndex

  if (Array.isArray(valorBruto) && valorBruto.length > 1 && Number.isInteger(blockIndex) && blockIndex >= 0 && blockIndex < valorBruto.length) {
    const nome = nomeDeItem(valorBruto[blockIndex])
    return nome === undefined || nome === null ? [] : [nome]
  }

  // Ancestral exclusivo desta instância (ou `blockIndex` não aplicável): todos os
  // nomes da seleção contam como candidatos.
  const nomeCompleto = getReplyValue(ancestor)
  return typeof nomeCompleto === 'string' ? nomeCompleto.split(',').map(nome => nome.trim()) : []
}

const instanciaCasaComAlvo = (nomesCandidatos, nomeAlvo) => {
  const alvoNormalizado = normalizarNomeRisco(nomeAlvo)
  return nomesCandidatos.some(nome => normalizarNomeRisco(nome) === alvoNormalizado)
}

/**
 * Lista, para este Cargo, cada instância respondida de "Possível Risco" +
 * Probabilidade + Severidade (mesma travessia par-a-par já usada antes desta
 * reescrita: cada posição de `INVENTARIO_PROBABILIDADE_QUESTION_IDS`/
 * `INVENTARIO_SEVERIDADE_QUESTION_IDS` que for descendente do Cargo é uma instância).
 * `buildLinhasFixas` casa cada uma dessas instâncias com sua linha fixa pelo nome.
 */
const buildCatalogoInstancias = ({ replies, descendantSet, blockIndex }) => {
  const instancias = []
  const total = Math.min(INVENTARIO_PROBABILIDADE_QUESTION_IDS.length, INVENTARIO_SEVERIDADE_QUESTION_IDS.length)

  for (let i = 0; i < total; i++) {
    const probQuestionId = INVENTARIO_PROBABILIDADE_QUESTION_IDS[i]
    const sevQuestionId = INVENTARIO_SEVERIDADE_QUESTION_IDS[i]

    // Basta um dos dois IDs do par aparecer na árvore deste Cargo — Probabilidade e
    // Severidade podem ter sido respondidas de forma independente.
    if (!belongsToCargo(descendantSet, probQuestionId) && !belongsToCargo(descendantSet, sevQuestionId)) continue

    // Vários candidatos só existem quando o bloco de Cargo repete (ver `preferirPorBlockIndex`
    // acima) — nesse caso, escolhe a resposta desta repetição específica pelo `blockIndex`.
    // Com 0/1 candidato (o caso já validado antes desta correção), comportamento inalterado.
    const probReply = preferirPorBlockIndex(filterRepliesByQuestionIds(replies, [probQuestionId]), blockIndex)
    const sevReply = preferirPorBlockIndex(filterRepliesByQuestionIds(replies, [sevQuestionId]), blockIndex)

    instancias.push({
      nomesCandidatos: getNomesRiscoCandidatos(replies, probReply || sevReply),
      probReply,
      sevReply
    })
  }

  return instancias
}

// `instancias` é consumida (splice) conforme cada linha fixa é resolvida: uma vez que
// uma instância é usada por uma linha, ela some do conjunto disponível para as
// próximas — evita que duas linhas fixas distintas peguem a MESMA instância por
// coincidência (ex.: fallback do caso (1) acima, onde vários nomes de uma seleção
// compartilhada são candidatos simultâneos).
const encontrarInstanciaPorNome = (instancias, nomeAlvo) => {
  const indice = instancias.findIndex(instancia => instanciaCasaComAlvo(instancia.nomesCandidatos, nomeAlvo))
  if (indice === -1) return null
  return instancias.splice(indice, 1)[0]
}

/**
 * Igual a `encontrarInstanciaPorNome`, mas NUNCA consome (nunca `splice`) a instância
 * encontrada — usada exclusivamente para a(s) linha(s) "Psicossocial" (ver
 * `LINHAS_INVENTARIO_RISCOS_FIXAS`/`inventarioRiscosFixo.js`: o modelo de referência
 * repete essa linha fixa 2x com conteúdo idêntico, mas a checklist só tem UMA pergunta
 * "Riscos do cargo" -> "Psicossocial" -> "Possível risco" por Cargo — nunca duas
 * respostas independentes). Bug real encontrado (2026-09-08, ao verificar a
 * especificação de Julia Buzzo/PM para a Seção 18): com `encontrarInstanciaPorNome`
 * (consumidora), a PRIMEIRA ocorrência da linha duplicada consumia a única instância
 * respondida — a SEGUNDA ocorrência não encontrava mais nada, caía no default de
 * ausência ("Muito Baixo"/"Irrelevante"), valores que a Matriz Psicossocial (vocabulário
 * próprio, ver `psicossocialRiskEngine.js`) não reconhece — resultando em Nível de Risco
 * = FALLBACK ("N/A") na segunda linha, mesmo com a atividade respondendo o risco
 * Psicossocial normalmente. Nunca consumir aqui é seguro: `nomesCandidatos` só casa com
 * o nome "Fatores Psicossociais relacionados ao trabalho" (normalizado), que não
 * coincide com nenhuma outra linha fixa — deixar a instância disponível não afeta as
 * demais linhas 'catalogo'.
 */
const encontrarInstanciaPorNomeSemConsumir = (instancias, nomeAlvo) =>
  instancias.find(instancia => instanciaCasaComAlvo(instancia.nomesCandidatos, nomeAlvo)) || null

// ATUALIZAÇÃO (2026-09-09): as listas estáticas `RUIDO_QUESTION_IDS`/
// `TEMPERATURA_QUESTION_IDS`/`ILUMINANCIA_QUESTION_IDS` (removidas do import acima)
// foram confirmadas ERRADAS por consulta direta ao banco de dados do workspace
// "bradesco" (tabela `checkList`, `serviceId` do PGR = 2): seus IDs (ex.: 276, 684,
// 688...) pertencem a uma árvore de "Possível Risco" (tipo `asset`) completamente
// diferente — sem NENHUMA relação com `CARGO_QUESTION_IDS`/"Riscos do cargo" — e nem
// um único ID dessas listas aparece como descendente real de nenhum dos 20 Cargos
// testados. Substituídas por casamento estrutural (nome + árvore de descendentes),
// mesmo padrão já usado para Biomecânico/Acidente/Psicossocial (`getNomesRiscoCandidatos`
// acima), confirmado via consulta real ao banco:
//
// Para cada posição de Cargo, "Ruído - dB (a)"/"Temperatura"/"Iluminância (lux)" são
// perguntas-filhas DIRETAS de "Riscos do cargo" daquela posição (mesmo `parentReference`
// — nunca aninhadas sob "Possível Risco"). O checklist real tem uma inconsistência de
// construção: a mesma pergunta (ex. "Ruído - dB (a)") aparece DUPLICADA uma vez por
// categoria de "Riscos do cargo" (Conforto/Biomecânico/Psicossocial/Acidente — cada
// cópia com seu próprio `display_conditions` gated na categoria selecionada), não só
// sob "Conforto" como o nome sugere — mas só a cópia da categoria realmente marcada
// pelo usuário chega a ser respondida (as demais nunca aparecem na atividade, logo
// nunca têm reply) — por isso basta pegar, entre os descendentes do Cargo com o nome
// certo, o que tiver uma resposta real.
// Já normalizados (sem acento) — comparados contra `normalizarNomeRisco(r.question?.name)`.
const NOME_MEDIDA_CONFORTO = {
  'conforto-ruido': normalizarNomeRisco('Ruído'),
  'conforto-temperatura': normalizarNomeRisco('Temperatura'),
  'conforto-iluminancia': normalizarNomeRisco('Iluminância')
}

const REGRAS_CONFORTO = {
  'conforto-ruido': REGRA_RUIDO,
  'conforto-temperatura': REGRA_TEMPERATURA,
  'conforto-iluminancia': REGRA_ILUMINANCIA
}

/**
 * Resolve a(s) instância(s) manuais de Probabilidade/Severidade de uma medição de
 * Conforto fora da faixa classificável automaticamente pela regra.
 *
 * CONFIRMADO por consulta direta ao banco (2026-09-09, tabela `checkList`): quando o
 * valor de Ruído/Temperatura/Iluminância está FORA da faixa "segura" (`regra(...)`
 * retorna `null`), o checklist real revela uma pergunta condicional própria de
 * Probabilidade e outra de Severidade, ambas FILHAS DIRETAS da pergunta de medição
 * (mesmo `question.reference` da medição como `parentReference` das duas) — os limiares
 * de exibição batem exatamente com `REGRA_RUIDO`/`REGRA_TEMPERATURA`/`REGRA_ILUMINANCIA`
 * (Ruído: aparece se > 65; Temperatura: se fora de 18–25; Iluminância: se < 500).
 * Antes desta correção, esse par condicional nunca era lido — a linha caía sempre no
 * FALLBACK "N/A" quando a medição estava fora da faixa, mesmo com a atividade
 * respondendo Probabilidade/Severidade manualmente.
 */
const buildLinhaManualConforto = (replies, medicaoReply) => {
  const probManual = replies.find(
    r => r.question?.parentReference === medicaoReply.question?.reference && normalizarNomeRisco(r.question?.name) === 'probabilidade'
  )
  const sevManual = replies.find(
    r => r.question?.parentReference === medicaoReply.question?.reference && normalizarNomeRisco(r.question?.name) === 'severidade'
  )
  return {
    probabilidadeTexto: probManual ? getReplyValue(probManual) : FALLBACK,
    severidadeTexto: sevManual ? getReplyValue(sevManual) : FALLBACK
  }
}

// Ruído/Temperatura/Iluminância: casamento por NOME + árvore de descendentes do Cargo
// (ver ATUALIZAÇÃO acima) + regra de faixa. Regra de negócio confirmada por Julia Buzzo
// (PM, 2026-08-27): quando a resposta está genuinamente ausente, a linha nunca é
// omitida — o lado ausente recebe o default lógico "Muito Baixo"/"Irrelevante" (que a
// Matriz de Risco resolve para "Trivial" normalmente). Um valor PRESENTE porém fora da
// faixa classificável pela regra (`regra(valorBruto)` retorna null) agora busca a
// pergunta manual condicional real (`buildLinhaManualConforto`) em vez de cair
// diretamente no FALLBACK.
const buildLinhaConforto = ({ replies, descendantSet, definicaoFixa, blockIndex }) => {
  const regra = REGRAS_CONFORTO[definicaoFixa.tipo]
  const nomeAlvo = NOME_MEDIDA_CONFORTO[definicaoFixa.tipo]
  const candidatos = replies.filter(
    r =>
      belongsToCargo(descendantSet, r.question?.id) &&
      (normalizarNomeRisco(r.question?.name) || '').includes(nomeAlvo) &&
      getRawReplyValue(r) !== undefined
  )
  const reply = preferirPorBlockIndex(candidatos, blockIndex)
  const valorBruto = reply ? getRawReplyValue(reply) : undefined

  if (isValorAusente(valorBruto)) {
    return { ...definicaoFixa, probabilidadeTexto: 'Muito Baixo', severidadeTexto: 'Irrelevante' }
  }

  const classificacao = regra(valorBruto)
  if (classificacao) {
    return { ...definicaoFixa, probabilidadeTexto: classificacao.probabilidade, severidadeTexto: classificacao.severidade }
  }

  return { ...definicaoFixa, ...buildLinhaManualConforto(replies, reply) }
}

// Biomecânico/Acidente (as 9 linhas "de catálogo", exceto Psicossocial): mesma regra de
// ausência do conforto, mas aplicada de forma independente por lado (Probabilidade e
// Severidade podem ter cada uma sua própria origem/ausência).
//
// Reforço (2026-09-02, verificação da nova regra de Probabilidade/Severidade/Nível de
// Risco pedida pela Julia Buzzo): a checagem de ausência usa o valor CRU
// (`probValorBruto`/`sevValorBruto`, via `getRawReplyValue`) porque só ele distingue
// "sem resposta" de "0"/string vazia. Mas o valor exibido em `probabilidadeTexto`/
// `severidadeTexto` — usado tanto para o texto da coluna em `18.InventarioRiscosSection.js`
// quanto como entrada de `calcularNiveisDeRisco` (ver `buildCargoInventario` abaixo) —
// precisa ser sempre um texto/número simples, nunca o objeto/array cru: se a resposta
// chegar como opção `{ id, name }` ou envelopada em array (`[{ id, name }]`, mesmo shape
// de outras perguntas de opção do checklist — ver `normalizeValueToText` em
// `pdfHelpers.js`), renderizar o valor cru direto na coluna quebraria o React (erro #31,
// objeto/array de objetos não é filho de JSX válido). `getReplyValue` já resolve esse
// shape para texto de forma segura — reaproveitado aqui só quando a resposta não está
// ausente, para não perder o default textual de ausência ('Muito Baixo'/'Irrelevante').
const buildLinhaCatalogo = ({ definicaoFixa, instanciaEncontrada }) => {
  const probValorBruto = instanciaEncontrada ? getRawReplyValue(instanciaEncontrada.probReply) : undefined
  const sevValorBruto = instanciaEncontrada ? getRawReplyValue(instanciaEncontrada.sevReply) : undefined

  return {
    ...definicaoFixa,
    probabilidadeTexto: isValorAusente(probValorBruto)
      ? 'Muito Baixo'
      : getReplyValue(instanciaEncontrada.probReply),
    severidadeTexto: isValorAusente(sevValorBruto) ? 'Irrelevante' : getReplyValue(instanciaEncontrada.sevReply)
  }
}

// Fatores Psicossociais: única linha que NÃO usa a Matriz de Risco principal — ver
// `psicossocialRiskEngine.js`.
const buildLinhaPsicossocial = ({ definicaoFixa, instanciaEncontrada }) => {
  const probValorBruto = instanciaEncontrada ? getRawReplyValue(instanciaEncontrada.probReply) : undefined
  const sevValorBruto = instanciaEncontrada ? getRawReplyValue(instanciaEncontrada.sevReply) : undefined

  // RESOLVIDO (2026-09-08, Julia Buzzo/PM — substitui o ABERTO anterior desta linha):
  // print do checklist real confirma que as perguntas de Probabilidade (1146) e
  // Severidade (1147) do Psicossocial têm vocabulário PRÓPRIO, e "Muito Baixo" já é a
  // opção nativa de ausência de resultado NESSE vocabulário em AMBAS as perguntas
  // (texto de ajuda literal: "Caso o resultado aqui seja 0 (pois não há resultados
  // disponíveis ainda), selecione a opção Muito Baixo") — nunca o "Irrelevante" do
  // vocabulário da Matriz de Risco PRINCIPAL (`riskEngine.js`, usado pelas demais
  // linhas 'catalogo'/'conforto'), que era o bug real do default anterior aqui.
  //
  // ATUALIZAÇÃO (2026-09-10, Julia Buzzo/PM): quando os dois lados chegam aqui como
  // "Muito Baixo" (seja por resposta real nos dois eixos, seja pelo default de ausência
  // acima), `resolveLinhaPsicossocial` (`psicossocialRiskEngine.js`) não passa mais pela
  // Matriz Final — devolve as três colunas como "Aprofundar" diretamente. Isso substitui
  // o resultado anterior desta mesma linha (Muito Baixo/Muito Baixo/Aceitável, PR #755,
  // já em produção).
  const probabilidadeBruta = isValorAusente(probValorBruto) ? 'Muito Baixo' : probValorBruto
  const severidadeBruta = isValorAusente(sevValorBruto) ? 'Muito Baixo' : sevValorBruto

  const resolvido = resolveLinhaPsicossocial(probabilidadeBruta, severidadeBruta)

  return {
    ...definicaoFixa,
    probabilidadeTexto: resolvido.probabilidadeTexto,
    severidadeTexto: resolvido.severidadeTexto,
    nivelDeRiscoPsicossocial: resolvido.nivelDeRisco
  }
}

const buildLinhasFixas = ({ replies, descendantSet, blockIndex }) => {
  const instanciasCatalogo = buildCatalogoInstancias({ replies, descendantSet, blockIndex })

  return LINHAS_INVENTARIO_RISCOS_FIXAS.map(definicaoFixa => {
    if (definicaoFixa.tipo.startsWith('conforto-')) {
      return buildLinhaConforto({ replies, descendantSet, definicaoFixa, blockIndex })
    }

    // Psicossocial: nunca consome a instância (`encontrarInstanciaPorNomeSemConsumir`,
    // ver definição acima) — a linha fixa repete 2x no template, mas só existe UMA
    // resposta possível por Cargo; as duas ocorrências devem refletir a MESMA resposta.
    if (definicaoFixa.tipo === 'psicossocial') {
      const instanciaEncontrada = encontrarInstanciaPorNomeSemConsumir(instanciasCatalogo, definicaoFixa.possivelRisco)
      return buildLinhaPsicossocial({ definicaoFixa, instanciaEncontrada })
    }

    const instanciaEncontrada = encontrarInstanciaPorNome(instanciasCatalogo, definicaoFixa.possivelRisco)
    return buildLinhaCatalogo({ definicaoFixa, instanciaEncontrada })
  })
}

/**
 * Lista de TODOS os Setores respondidos na atividade, sem duplicar — combinando as DUAS
 * perguntas de Setor (`SETOR_QUESTION_IDS` = [256, 354], ver `mappingIds.js` para a causa
 * raiz completa desta regra, confirmada por Julia Buzzo/PM em 2026-09-03). Cada resposta
 * de 256 (checkbox multi-select) é desembrulhada em um nome por seleção
 * (`getNomesDaResposta`); 354 é uma pergunta dentro de um bloco REPETÍVEL ("Adicione os
 * setores" -> "Adicionar novo item", cada clique gera uma repetição/instância própria) —
 * `filterRepliesByQuestionIds` abaixo é um `.filter()`, não um `.find()`, então já reúne
 * a resposta de TODAS as repetições existentes em `replies` (nunca só a primeira ou a
 * última — confirmado explicitamente por Julia Buzzo, PM, 2026-09-03, ver teste sintético
 * com 3+ repetições em `scratchpad/test_setor_256_354.mjs`), cada uma contando como seu
 * próprio Setor. Preserva a ordem de aparição em `replies` (normalmente 256 antes de 354,
 * pela ordem estrutural da checklist, mas não é uma garantia desta função) — nunca usada
 * para pareamento posicional, só para enumerar a lista completa de Setores.
 */
export const getListaDeSetoresRespondidos = (replies = []) => {
  const nomes = filterRepliesByQuestionIds(replies, SETOR_QUESTION_IDS).flatMap(getNomesDaResposta)
  return Array.from(new Set(nomes))
}

/**
 * Retorna a resposta (texto) do Setor da atividade, combinando as DUAS perguntas de
 * Setor (256 e 354) numa única string — mesmo formato de sempre (nomes unidos com ", ",
 * comportamento herdado de quando só existia a pergunta 256: reply com 3 itens
 * selecionados devolvia "BDN, Retaguarda Administrativa, Gerência Administrativa").
 *
 * Usada como FALLBACK por `buildCargoInventario`/`listarSetoresComCargos` abaixo quando o
 * pareamento estrutural por posição (`getSetorPorPosicaoDeCargo`) não encontra nada para
 * uma posição — nunca mais como o único valor aplicado a TODOS os blocos de Cargo (isso
 * era a limitação original, RESOLVIDA — ver `getSetorPorPosicaoDeCargo`).
 */
export const getSetor = replies => {
  const nomes = getListaDeSetoresRespondidos(replies)
  return nomes.length ? nomes.join(', ') : FALLBACK
}

// Marca a pergunta "Tire foto de cada posto de trabalho do setor [NOME DO SETOR]" (ou
// variação de plural/artigo) usada como MARCADOR posicional em `getSetorPorPosicaoDeCargo`
// abaixo, para os Setores vindos da pergunta 256 — nunca para exibição (o texto da
// pergunta em si não aparece em nenhuma seção). A pergunta-marcador do fluxo alternativo
// de Setor por texto livre (354) NÃO tem esse "[NOME]" interpolado (é um texto genérico,
// "Tire foto de cada posto de trabalho do setor", confirmado por vídeo da Julia,
// 2026-09-03) — por isso esse fluxo é resolvido por `blockIndex` abaixo, não por este regex.
const MARCADOR_SETOR_REGEX = /posto[s]? de trabalho.*?setor(?:es)?\s*\[(.+?)\]/i

/**
 * LIMITAÇÃO RESOLVIDA (2026-09-01, 4ª rodada de investigação do item Setor/Cargo — ver
 * `sections/05.MetodologiaSection.js` para o histórico completo das 3 rodadas
 * anteriores): Julia Buzzo (PM) confirmou por vídeo da tela real do builder/preview do
 * checklist do Bradesco que:
 *
 * 1. Setor (256) NÃO compartilha bloco/blockIndex com Cargo (descarta a hipótese de
 *    blockIndex compartilhado da 2ª rodada).
 * 2. Cargo está dentro de um bloco "Características do cargo" que é, ele mesmo, uma
 *    pergunta condicionada a uma opção específica de Setor — cada Setor selecionado em
 *    256 tem sua própria pergunta-marcador "Tire foto de cada posto de trabalho do setor
 *    [NOME DO SETOR]" (pergunta de foto, sem relação de conteúdo com Cargo — usada aqui
 *    SÓ como marcador posicional), imediatamente seguida, na ORDEM de exibição da
 *    checklist (mesmo `ordination` usado por `sortReplies`/`sortQuestions` em todo o
 *    app), pelo(s) bloco(s) "Características do cargo" daquele mesmo Setor. Confirmado
 *    por inspeção direta da tela de Simulação/preview do checklist real do Bradesco: a
 *    pergunta 682 "Características do cargo" (cujo Cargo (GHE) é a pergunta 662) aparece
 *    imediatamente após a pergunta 257 "Tire foto de cada posto de trabalho do setor
 *    [Gerência Agência]" — ambas exibidas pelo preview da própria plataforma sob a mesma
 *    legenda de pergunta-pai "Quais os setores existem nesta dependência?" (= pergunta
 *    256). Mesmo padrão confirmado para outro par (bloco 791 "Características do cargo",
 *    Cargo 792, logo após a pergunta 263 "Tire foto de cada posto de trabalho do setor
 *    [Retaguarda Administrativa]").
 * 3. A lista de opções do dropdown "Cargo (GHE)" é a MESMA lista completa (ordem
 *    alfabética, ~todos os cargos da empresa) em QUALQUER posição, independente do
 *    Setor — ou seja, o Cargo escolhido dentro de um bloco NÃO é restrito ao Setor
 *    daquele bloco nem casa por nome/grupo com ele. O vínculo Setor->Cargo está
 *    inteiramente na ESTRUTURA/ORDEM da checklist (qual bloco de Cargo fica disponível
 *    sob qual Setor selecionado), nunca no nome do Cargo escolhido pelo usuário nem em
 *    metadado da definição da pergunta (grupo/categoria de opção — já descartado na 3ª
 *    rodada, ver histórico).
 *
 * `getSetorPorPosicaoDeCargo` deriva, por ORDENAÇÃO (`question.ordination`, não
 * `blockIndex`), qual Setor "está em vigor" no momento em que cada posição de
 * `CARGO_QUESTION_IDS` é alcançada ao percorrer as replies em ordem de exibição —
 * atualizando esse Setor "em vigor" sempre que uma pergunta-marcador (regex acima) é
 * encontrada. NÃO é pareamento por índice/posição arbitrário (isso continua vetado): é a
 * ORDEM REAL de exibição da checklist, a mesma que o usuário viu ao responder.
 *
 * ATUALIZAÇÃO (2026-09-03, regra definitiva de Setor — 256+354, ver `mappingIds.js`):
 * Cargo (posição 681 de `CARGO_QUESTION_IDS`, confirmado por vídeo da Julia) pode viver
 * dentro do fluxo alternativo "Adicione os setores" (Setor por texto livre, pergunta 354),
 * cuja pergunta-marcador de foto NÃO tem o "[NOME]" interpolado — o regex acima nunca
 * casa para esse fluxo, então o Setor "em vigor" por ordenação ficaria indefinido para
 * essas posições. Resolvido por um caminho ADICIONAL, checado primeiro por posição: se
 * existir uma resposta à pergunta 354 com o MESMO `blockIndex` da resposta de Cargo
 * daquela posição (mesmo "item" do "Adicionar novo item", mesmo padrão de agrupamento
 * por `blockIndex` já usado em outros PDFs — ver contexto técnico do módulo), o nome
 * respondido em 354 é o Setor dessa posição — mais preciso que o marcador por ordenação,
 * porque é um vínculo estrutural direto (mesmo bloco), não uma inferência por ordem de
 * exibição. Se não houver esse vínculo direto, cai no marcador por ordenação (caminho
 * 256, inalterado) e, por fim, no fallback global de `getSetor()`.
 *
 * Retorna um array paralelo a `CARGO_QUESTION_IDS` (mesmo índice) com o nome do Setor
 * encontrado para aquela posição, ou `undefined` quando nenhum dos dois caminhos resolve
 * nada (ex.: atividade antiga/diferente sem essa estrutura) — o chamador cai no fallback
 * de `getSetor()` global nesse caso, nunca quebra.
 */
export const getSetorPorPosicaoDeCargo = (replies = []) => {
  const ordenadas = [...replies].sort((a, b) => (a.question?.ordination ?? 0) - (b.question?.ordination ?? 0))
  const respostasSetorTextoLivre = filterRepliesByQuestionIds(replies, [354])

  const setorPorPosicao = new Array(CARGO_QUESTION_IDS.length).fill(undefined)
  let setorEmVigor

  ordenadas.forEach(reply => {
    const nomePergunta = reply.question?.name
    const match = typeof nomePergunta === 'string' ? nomePergunta.match(MARCADOR_SETOR_REGEX) : null
    if (match) {
      setorEmVigor = match[1].trim()
      return
    }

    const posicao = CARGO_QUESTION_IDS.indexOf(reply.question?.id)
    if (posicao === -1 || setorPorPosicao[posicao] !== undefined) return

    // Caminho direto (354, mesmo blockIndex) — checado antes do marcador por ordenação,
    // ver ATUALIZAÇÃO acima.
    const setorTextoLivre = respostasSetorTextoLivre.find(
      setorReply => setorReply.blockIndex !== undefined && setorReply.blockIndex === reply.blockIndex
    )
    if (setorTextoLivre) {
      const nomes = getNomesDaResposta(setorTextoLivre)
      setorPorPosicao[posicao] = nomes.length ? nomes.join(', ') : undefined
      return
    }

    setorPorPosicao[posicao] = setorEmVigor
  })

  return setorPorPosicao
}

/**
 * Nome(s) de Cargo respondido(s) numa ÚNICA posição de `CARGO_QUESTION_IDS`.
 *
 * Bug relatado por Julia Buzzo (PM, 2026-08-30, imagens 1-3 do Slack, Seção 6; e
 * 2026-08-30, imagens 1-2, Seção 18 — mesma causa raiz nas duas seções): cada posição é
 * uma pergunta "checkbox" (multi-select) — o normal é uma seleção única, mas o
 * formulário permite mais de um Cargo dentro da MESMA posição (ex.: variações de nível
 * do mesmo Cargo, como "ADMINISTRATIVO TI SR/JR/PL" ou "ADVISOR I/II/III", cada grupo
 * selecionado de uma vez, na mesma pergunta). `getReplyValue` (correto para o caso de
 * seleção única) reduz esse array a UMA string única unindo os nomes com ", "
 * (`normalizeValueToText` em `pdfHelpers.js`). Nenhum Ativo tem esse nome combinado como
 * `name` — o casamento por nome (`assets.find(item => item.name === nomeCargo)`, mesmo
 * padrão de manserv/comercial/segurpro) nunca encontra o Ativo, e a linha inteira cai no
 * fallback `N/A`, exibindo os Cargos MERGEADOS numa única linha/bloco em vez de uma
 * linha/bloco por Cargo (ver modelo de referência, imagem 2 da Julia — cada Cargo sempre
 * com sua própria linha/bloco).
 *
 * Usada por `listarCargosComDescricao` (Seção 6) e por `buildCargoInventario` (Seção 18,
 * ver comentário lá para a extensão desta correção a essa seção) para devolver um nome
 * por item selecionado, permitindo casar cada um individualmente com seu próprio Ativo.
 * Mesma lógica de `getListaDeSetoresRespondidos` acima (`getNomesDaResposta`), reaproveitada
 * aqui por serem perguntas do mesmo tipo "checkbox" com o mesmo problema de shape.
 */
const getNomesCargoDaResposta = getNomesDaResposta

/** Casa um nome de Cargo com seu Ativo (mesmo padrão de casamento por nome de sempre) e resolve sua Descrição. */
const resolverCargoPorNome = (assets, nomeCargo) => {
  const asset = assets.find(item => item.name === nomeCargo)
  const descricaoCargo = asset
    ? getFieldValueByIdOrFallback(asset.fieldValues, ATIVO_FIELD_ID_DESCRICAO_CARGO)
    : FALLBACK

  return { cargo: nomeCargo, descricaoCargo }
}

/**
 * TODAS as respostas VÁLIDAS (com valor) de uma posição de `CARGO_QUESTION_IDS` — uma por
 * repetição existente do bloco que contém aquela pergunta (ver ATUALIZAÇÃO abaixo). Antes
 * desta correção (2026-09-03), cada função usava `findReplyByQuestionId` (uma única
 * reply, a primeira encontrada) para Cargo — correto só enquanto cada posição tinha, no
 * máximo, UMA reply na atividade. `filterRepliesByQuestionIds` é um `.filter()`, não um
 * `.find()`, então já reúne toda reply existente para aquele id — usado aqui para nunca
 * mais perder Cargos de repetições além da primeira.
 *
 * ATUALIZAÇÃO (2026-09-03, regra definitiva de Julia Buzzo/PM, ampliando a regra já
 * aplicada a Setor no mesmo dia): TODOS os 20 `CARGO_QUESTION_IDS` — não só a posição 681
 * (a única com bloco repetível confirmado até então) — devem ser percorridos considerando
 * TODAS as ocorrências/repetições de bloco existentes na atividade, nunca só a primeira
 * ou a última. Combinado com o desembrulho de seleção múltipla já existente
 * (`getNomesCargoDaResposta`), a regra final é: por posição, por repetição, por Cargo
 * selecionado dentro dessa repetição — "1 Cargo = 1 registro/tabela própria", em toda
 * seção do documento com tabela relacionada a Cargo (Seção 6, Seção 18, Seção 5.3).
 */
const getRespostasDeCargo = (replies, cargoQuestionId) =>
  filterRepliesByQuestionIds(replies, [cargoQuestionId]).filter(reply => getRawReplyValue(reply) !== undefined)

/**
 * Lista os cargos respondidos na atividade com sua Descrição do Cargo — usado pela
 * Seção 6 (Descrição das Atividades). Mais leve que `buildCargoInventario` (não
 * calcula as linhas de risco da Seção 18).
 *
 * Uma posição de `CARGO_QUESTION_IDS` pode ter MAIS de uma resposta (uma por repetição de
 * bloco, ver `getRespostasDeCargo` acima) e cada resposta pode resolver em MAIS de um
 * Cargo (seleção múltipla, ver `getNomesCargoDaResposta`) — cada combinação
 * posição×repetição×Cargo se torna sua própria linha, com Ativo e Descrição resolvidos
 * individualmente (nunca agrupados numa linha só).
 */
export const listarCargosComDescricao = (replies = [], assets = []) => {
  // Mesmo cuidado de `buildCargoInventario` abaixo: isola cada posição para que um
  // problema num único cargo (ex.: Ativo sem `fieldValues`) não derrube a lista inteira
  // e, com isso, os demais cargos válidos da Seção 6.
  return CARGO_QUESTION_IDS.flatMap(cargoQuestionId => {
    try {
      return getRespostasDeCargo(replies, cargoQuestionId).flatMap(cargoReply =>
        getNomesCargoDaResposta(cargoReply).map(nomeCargo => resolverCargoPorNome(assets, nomeCargo))
      )
    } catch (erro) {
      // eslint-disable-next-line no-console
      console.error(`[PGR Bradesco] Falha ao resolver o cargo da pergunta ${cargoQuestionId} na Seção 6; os demais cargos continuam sendo listados normalmente.`, erro)
      return []
    }
  })
}

/**
 * Monta a lista de GHEs (um por Cargo — nunca agrupado por Setor, ver comentário no
 * topo do arquivo) presentes na atividade, com os dados gerais de cada cargo e as 13
 * linhas FIXAS do Inventário de Riscos (`_helpers/inventarioRiscosFixo.js`) já
 * resolvidas (Probabilidade/Severidade/Nível de Risco por linha, ver `buildLinhasFixas`).
 *
 * Bug relatado por Julia Buzzo (PM, 2026-08-30, imagens 1-2 do Slack) — MESMA causa raiz
 * já corrigida na Seção 6 (ver `getNomesCargoDaResposta` acima, PR
 * fix/pgr-section6-cargo-descricao): quando uma posição de `CARGO_QUESTION_IDS` tem mais
 * de um Cargo selecionado na MESMA pergunta (ex.: "ADMINISTRATIVO TI SR/JR/PL"), a
 * versão anterior desta função lia essa posição com `getReplyValue` — que junta a
 * seleção múltipla numa única string com ", " — e tentava casar essa string combinada
 * com um Ativo pelo nome; nenhum Ativo tem esse nome combinado, a Descrição caía em
 * `N/A`, e a Seção 18 renderizava UM bloco (cabeçalho + tabela de 13 linhas) só, com os
 * Cargos mesclados repetidos na coluna "GHE → Cargo" de toda linha — em vez de um bloco
 * completo e independente por Cargo (ver modelo de referência, imagem 2 da Julia, e o
 * comentário já existente em `sections/18.InventarioRiscosSection.js`, confirmado
 * pixel-a-pixel contra o PDF real: "o agrupamento é sempre por CARGO, nunca por Setor").
 * Confirmado empiricamente com harness (esbuild + Node, código real sem modificação para
 * o teste, ver descrição do PR) antes desta correção.
 *
 * Setor por posição (2026-09-01, ver `getSetorPorPosicaoDeCargo`): cada bloco de Cargo
 * agora mostra o Setor estruturalmente associado a ELE (pergunta-marcador "Tire foto...
 * do setor [X]" imediatamente anterior na ordem de exibição), em vez do único Setor
 * global repetido em todo bloco. Fallback para `getSetor()` global quando a posição não
 * resolve nenhum Setor estrutural (nunca deixa a coluna vazia).
 *
 * Fix: cada posição continua sendo processada uma única vez para os dados que a spec só
 * define por POSIÇÃO/PERGUNTA (nunca por Cargo individual dentro de uma seleção
 * múltipla) — Nº de Funcionários Expostos (`NUM_FUNCIONARIOS_QUESTION_IDS`, pareado por
 * posição) e as 13 linhas de Probabilidade/Severidade (`buildLinhasFixas`, resolvidas a
 * partir da árvore `parentReference` DESSA posição) — e só então expandida (`flatMap`,
 * nunca `map`) numa entrada por Cargo efetivamente selecionado nessa posição
 * (`getNomesCargoDaResposta` + `resolverCargoPorNome`, mesmas funções já confiáveis da
 * Seção 6). Posição com seleção única (a maioria dos casos): resultado idêntico ao
 * comportamento anterior (1 posição = 1 bloco). Posição com múltiplas seleções: cada
 * Cargo agora recebe seu próprio bloco completo — Nome e Descrição casados
 * individualmente pelo nome, mas necessariamente compartilhando o Nº de Funcionários
 * Expostos e as 13 linhas de risco DA POSIÇÃO, já que a spec fornecida não define uma
 * sub-pergunta de Probabilidade/Severidade por Cargo dentro da mesma seleção múltipla
 * (mesma limitação, aliás, que já existia — e persiste — para Nº de Funcionários
 * Expostos mesmo antes desta correção, num Cargo de seleção única).
 *
 * LIMITAÇÃO Setor↔Cargo RESOLVIDA (2026-09-01, 4ª rodada — ver `getSetorPorPosicaoDeCargo`
 * para a evidência completa): `setor` deixou de ser resolvido uma única vez para a
 * atividade inteira e aplicado igual a todos os blocos — agora cada posição usa o Setor
 * estruturalmente associado a ela (`setoresPorPosicao[index]`), com fallback para
 * `getSetor()` global só quando a posição não resolve nenhum Setor estrutural.
 *
 * ATUALIZAÇÃO (2026-09-03, regra definitiva de Julia Buzzo/PM — RESOLVE o item ABERTO
 * anterior desta seção, que só apontava o risco para a posição 681): confirmado que
 * TODOS os 20 `CARGO_QUESTION_IDS` devem ser percorridos considerando TODAS as
 * ocorrências/repetições de bloco existentes (não só 681, e não só a primeira/última) —
 * ver `getRespostasDeCargo` acima. Cada repetição de Cargo tem seu próprio Nº de
 * Funcionários Expostos e suas próprias 13 linhas de Probabilidade/Severidade,
 * resolvidos agora por `blockIndex` (`preferirPorBlockIndex`, ver definição acima) em vez
 * de "a primeira reply encontrada para este id, seja qual for" — sem isso, múltiplas
 * repetições da MESMA posição acabariam todas lendo os dados da PRIMEIRA repetição. O
 * Setor da posição (`setoresPorPosicao[index]`) continua compartilhado entre repetições
 * da MESMA posição — são todas do mesmo Setor estrutural (mesmo marcador/slot da
 * checklist), só a instância de "Características do cargo" que se repete dentro dele.
 */
export const buildCargoInventario = (replies = [], assets = []) => {
  const setorGlobal = getSetor(replies)
  const setoresPorPosicao = getSetorPorPosicaoDeCargo(replies)

  // Cada posição de CARGO_QUESTION_IDS é montada isoladamente (try/catch por posição):
  // um problema ao montar os dados de UM cargo (ex.: reply com formato inesperado numa
  // atividade real) não pode derrubar o `.flatMap()` inteiro e, com isso, apagar os
  // OUTROS cargos já válidos — ver requisito de Julia Buzzo (18/Inventário de Riscos,
  // 2026-08-27): "um problema com os dados de um cargo não pode impedir que os demais
  // cargos válidos sejam montados". Sem o try/catch, uma exceção em QUALQUER posição
  // (ex.: `assets` malformado ou uma resposta com `question` ausente) propagaria e
  // cancelaria a Seção 18 inteira.
  return CARGO_QUESTION_IDS.flatMap((cargoQuestionId, index) => {
    try {
      const cargoReplies = getRespostasDeCargo(replies, cargoQuestionId)
      if (cargoReplies.length === 0) return []

      // Setor desta posição: estrutural (marcador "Tire foto... do setor [X]" mais
      // recente antes desta posição na ordem de exibição, ou blockIndex-354) com
      // fallback para o Setor global — ver `getSetorPorPosicaoDeCargo`. Compartilhado
      // entre todas as repetições desta posição (ver ATUALIZAÇÃO acima).
      const setor = setoresPorPosicao[index] || setorGlobal

      // Uma posição pode ter MAIS de uma repetição de bloco (`cargoReplies`, ver
      // `getRespostasDeCargo`) e cada repetição pode resolver em MAIS de um Cargo
      // (seleção múltipla, `getNomesCargoDaResposta`) — cada combinação
      // repetição×Cargo se torna seu próprio bloco/GHE completo e independente.
      return cargoReplies.flatMap((cargoReply, repeticaoIndex) => {
        const numFuncionariosCandidatos = filterRepliesByQuestionIds(replies, [NUM_FUNCIONARIOS_QUESTION_IDS[index]])
        const numFuncionariosReply = preferirPorBlockIndex(numFuncionariosCandidatos, cargoReply.blockIndex)
        const numFuncionariosValorBruto = getRawReplyValue(numFuncionariosReply)
        // "Nenhuma resposta" para Nº de Funcionários Expostos não é o mesmo que "não sei"
        // — significa 0 pessoas expostas para este Cargo (nunca o placeholder textual
        // genérico "N/A" numa coluna numérica).
        const numFuncionariosExpostos = isValorAusente(numFuncionariosValorBruto) ? 0 : getReplyValue(numFuncionariosReply)

        // BUG CRÍTICO encontrado e corrigido (2026-09-09, ao gerar um PDF de exemplo com
        // dados 100% reais do banco para a PR #760): a raiz usada aqui era
        // `cargoReply.question?.reference` (o `reference` do próprio Cargo), mas
        // consulta direta ao banco (tabela `checkList`, posições 662/681/697) confirma
        // que "Riscos do cargo"/"Nº funcionarios expostos" são IRMÃOS do Cargo — têm o
        // MESMO `parentReference` que ele (o UUID do bloco compartilhado daquela
        // posição), não filhos do `reference` do Cargo. Com a raiz antiga, o Cargo não
        // tem NENHUM filho real na árvore (`getDescendantQuestionIds` retornava só a
        // própria raiz) — `belongsToCargo` nunca batia com nada, e TODA a Seção 18
        // (Conforto via nome+árvore E o catálogo de Possível Risco via
        // `buildCatalogoInstancias`, que também depende de `descendantSet`) caía sempre
        // no default de ausência, mesmo com respostas reais presentes. Confirmado que
        // cada posição de `CARGO_QUESTION_IDS` tem seu próprio `parentReference` de bloco
        // (662→f7ca3c49..., 681→46e97a9b..., 697→cff2dba3..., todos distintos) — usar o
        // `parentReference` do Cargo como raiz é seguro e não mistura dados entre
        // posições diferentes. `question.parentReference` é o mesmo para toda repetição
        // desta posição (mesma pergunta-template) — a árvore de descendentes não varia
        // por repetição, só a reply escolhida dentro dela (via `blockIndex`, abaixo).
        const descendantSet = getDescendantQuestionIds(replies, cargoReply.question?.parentReference)

        const linhas = buildLinhasFixas({ replies, descendantSet, blockIndex: cargoReply.blockIndex })

        // Nível de Risco: as linhas normais são resolvidas em lote pela Matriz de Risco
        // principal (posicional, Probabilidade[i] + Severidade[i] -> Nível[i], ver
        // `riskEngine.js`). A(s) linha(s) Psicossocial(is) já chegam com seu próprio
        // Nível de Risco resolvido pela Matriz Final (`nivelDeRiscoPsicossocial`) e NUNCA
        // passam pela Matriz principal — ver `psicossocialRiskEngine.js`.
        const linhasNormais = linhas.filter(linha => linha.tipo !== 'psicossocial')
        const niveisNormais = calcularNiveisDeRisco(
          linhasNormais.map(linha => linha.probabilidadeTexto),
          linhasNormais.map(linha => linha.severidadeTexto)
        )
        // Mapa por identidade (cada linha é um objeto novo, criado uma única vez em
        // `buildLinhasFixas`) — evita um contador mutável para "religar" `niveisNormais`
        // (calculado sobre a lista FILTRADA `linhasNormais`) de volta à ordem original de
        // `linhas`, que também contém a(s) linha(s) Psicossocial(is) fora dessa lista.
        const nivelPorLinhaNormal = new Map(linhasNormais.map((linha, i) => [linha, niveisNormais[i]?.classificacao]))

        const linhasComNivel = linhas.map(linha => ({
          ...linha,
          nivelDeRisco: (linha.tipo === 'psicossocial' ? linha.nivelDeRiscoPsicossocial : nivelPorLinhaNormal.get(linha)) || FALLBACK
        }))

        return getNomesCargoDaResposta(cargoReply).map((nomeCargo, nomeIndex) => ({
          index: `${index}-${repeticaoIndex}-${nomeIndex}`,
          setor,
          cargo: nomeCargo,
          numFuncionariosExpostos,
          descricaoCargo: resolverCargoPorNome(assets, nomeCargo).descricaoCargo,
          linhas: linhasComNivel
        }))
      })
    } catch (erro) {
      // eslint-disable-next-line no-console
      console.error(`[PGR Bradesco] Falha ao montar o(s) cargo(s) da posição ${index} (question id ${cargoQuestionId}); os demais cargos continuam sendo montados normalmente.`, erro)
      return []
    }
  })
}

/**
 * Agrupa os Cargos respondidos por Setor — uma LINHA POR SETOR (não uma única linha com
 * todos os Setores concatenados por vírgula numa célula e todos os Cargos noutra) — usada
 * pela Seção 5.3 (tabela "Setor | Cargo (GHE)", ver `sections/05.MetodologiaSection.js`
 * para o histórico completo da investigação e a evidência do PDF-modelo/vídeo da Julia).
 *
 * Reaproveita o pareamento estrutural de `getSetorPorPosicaoDeCargo` — cada Cargo
 * respondido entra na linha do Setor estruturalmente associado à sua posição.
 *
 * FALLBACK (nunca regride): se NENHUMA posição respondida resolver um Setor estrutural
 * (checklist sem o padrão de pergunta-marcador esperado, ou atividade antiga/diferente),
 * cai no comportamento anterior a esta correção — uma única linha com `getSetor()` global
 * e todos os Cargos juntos.
 *
 * ATUALIZAÇÃO (2026-09-03, regra definitiva de Julia Buzzo/PM, mesma de
 * `buildCargoInventario` acima): percorre TODAS as repetições de bloco de cada posição
 * (`getRespostasDeCargo`), não só a primeira — nenhum Cargo de uma repetição além da
 * primeira é perdido nesta tabela.
 */
export const listarSetoresComCargos = (replies = [], assets = []) => {
  const setoresPorPosicao = getSetorPorPosicaoDeCargo(replies)
  const cargosPorSetor = new Map() // nome do Setor -> [nomes de Cargo, sem duplicar]

  CARGO_QUESTION_IDS.forEach((cargoQuestionId, index) => {
    try {
      const cargoReplies = getRespostasDeCargo(replies, cargoQuestionId)
      if (cargoReplies.length === 0) return

      const setor = setoresPorPosicao[index]
      if (!setor) return // sem marcador estrutural para esta posição — cai no fallback abaixo

      if (!cargosPorSetor.has(setor)) cargosPorSetor.set(setor, [])
      const cargosDesteSetor = cargosPorSetor.get(setor)
      cargoReplies.forEach(cargoReply => {
        getNomesCargoDaResposta(cargoReply).forEach(nomeCargo => {
          if (!cargosDesteSetor.includes(nomeCargo)) cargosDesteSetor.push(nomeCargo)
        })
      })
    } catch (erro) {
      // eslint-disable-next-line no-console
      console.error(`[PGR Bradesco] Falha ao agrupar por Setor o cargo da posição ${index} (question id ${cargoQuestionId}) na Seção 5.3; os demais continuam sendo agrupados normalmente.`, erro)
    }
  })

  if (cargosPorSetor.size === 0) {
    const setor = getSetor(replies) || FALLBACK
    const cargos = listarCargosComDescricao(replies, assets).map(({ cargo }) => cargo)
    return [{ setor, cargos: cargos.length > 0 ? cargos : [FALLBACK] }]
  }

  return Array.from(cargosPorSetor.entries()).map(([setor, cargos]) => ({ setor, cargos }))
}

/**
 * Agrupa a lista PLANA de Cargos (`buildCargoInventario`) por Setor — usada pela Seção 18
 * (Inventário de Riscos) para garantir que cada Setor respondido (256 e/ou 354, ver
 * `mappingIds.js`) tenha seus Cargos reunidos sob o mesmo grupo, na ordem de primeira
 * aparição, em vez de intercalados com Cargos de outro Setor. Regra confirmada por Julia
 * Buzzo (PM, 2026-09-03): "cada Setor respondido deve gerar sua própria tabela — nunca
 * agrupar Setores numa única tabela".
 *
 * IMPORTANTE — o que esta função NÃO muda: cada Cargo continua gerando sua própria
 * caixa/tabela completa (13 linhas fixas), exatamente como já confirmado pixel-a-pixel
 * contra o PDF-modelo real (ver cabeçalho do arquivo e `18.InventarioRiscosSection.js`) —
 * "tabela de Setor" aqui é o AGRUPAMENTO/ordem dos blocos de Cargo daquele Setor, nunca
 * uma tabela única mesclando várias linhas de Cargos diferentes. Antes desta correção, o
 * bug real que essa regra resolve era outro: Cargos de um Setor cadastrado por texto
 * livre (354) sem vínculo estrutural (256) caíam no fallback `getSetor()` global — uma
 * string com TODOS os Setores de 256 concatenados por vírgula — fazendo Cargos de Setores
 * diferentes exibirem o MESMO rótulo "Setor:" incorreto/mesclado. Corrigido na origem em
 * `getSetorPorPosicaoDeCargo` (resolução por `blockIndex` para o caminho 354); esta função
 * só garante que, com o rótulo agora correto por Cargo, o agrupamento visual também
 * reflita isso sem misturar posições de Setores diferentes.
 *
 * ABERTO: não implementado aqui um cabeçalho/título visual NOVO por grupo de Setor (ex.:
 * "SETOR: X" antes do primeiro Cargo daquele grupo) — o modelo de referência já confirmado
 * (pixel-a-pixel) só mostra "Setor:" como rótulo DENTRO da caixa de cada Cargo, nunca um
 * título de seção separado; adicionar um exigiria confirmação visual contra o PDF-modelo
 * (fora do escopo desta correção, que é sobre a FONTE do dado, não o layout).
 */
export const agruparCargosPorSetor = (cargos = []) => {
  const ordem = []
  const cargosPorSetor = new Map()

  cargos.forEach(cargo => {
    const setor = cargo.setor || FALLBACK
    if (!cargosPorSetor.has(setor)) {
      cargosPorSetor.set(setor, [])
      ordem.push(setor)
    }
    cargosPorSetor.get(setor).push(cargo)
  })

  return ordem.map(setor => ({ setor, cargos: cargosPorSetor.get(setor) }))
}
